import { useMemo } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useInterfaceMode } from "@/hooks/useInterfaceMode";
import {
  Sun,
  Moon,
  Apple,
  Heart,
  ShieldCheck,
} from "lucide-react";
import type { SessionRecord } from "@/hooks/useSessions";

interface SessionAdviceBlockProps {
  session: SessionRecord;
}

/* ── Tiny rules engine ────────────────────────────────────
   Derives contextual tips from session metadata.
   No API call, no generation — pure local derivation.
   ───────────────────────────────────────────────────── */

interface AdviceSection {
  key: string;
  icon: React.ElementType;
  title: string;
  tips: string[];
  detailedTips?: string[]; // extra tips shown in standard/advanced only
}

function deriveAdvice(session: SessionRecord): AdviceSection[] {
  const tags = (session.tags || []).map((t) => t.toLowerCase());
  const objective = (session.objective || "").toLowerCase();
  const level = (session.level_required || "").toLowerCase();
  const duration = session.duration_minutes || 0;
  const exerciseCount = session.exercises?.length || 0;
  const exerciseNames = (session.exercises || []).map((e: any) => (e.name || "").toLowerCase());

  const isStrength = tags.some((t) => ["force", "hypertrophie", "musculation", "strength"].includes(t)) || objective.includes("force") || objective.includes("muscle");
  const isCardio = tags.some((t) => ["cardio", "endurance", "hiit", "course"].includes(t)) || objective.includes("cardio") || objective.includes("endurance");
  const hasLegs = exerciseNames.some((n) => /squat|fente|leg|jambe|mollet|ischio|quad|deadlift|soulevé/i.test(n));
  const hasUpperBody = exerciseNames.some((n) => /bench|press|curl|tricep|épaule|dos|rowing|tirage|dips|push/i.test(n));
  const isLong = duration >= 60;
  const isBeginner = level.includes("débutant") || level.includes("beginner");

  const sections: AdviceSection[] = [];

  // ── Before session ──
  const beforeTips: string[] = [];
  const beforeDetailed: string[] = [];
  beforeTips.push("Hydrate-toi : 300–500 ml d'eau 30 min avant.");
  if (isStrength) {
    beforeTips.push("Échauffement articulaire 5 min + séries progressives légères.");
    beforeDetailed.push("Commence par 2 séries à 50 % de ta charge de travail pour activer le système nerveux.");
  } else if (isCardio) {
    beforeTips.push("Échauffement progressif 5–10 min à basse intensité.");
    beforeDetailed.push("Augmente progressivement la fréquence cardiaque pour éviter un pic de stress.");
  } else {
    beforeTips.push("Échauffement général 5 min : mobilité + activation musculaire.");
  }
  if (hasLegs) {
    beforeTips.push("Active tes fessiers et ischio-jambiers avec des ponts ou marches latérales.");
  }
  if (isBeginner) {
    beforeDetailed.push("Prends le temps de bien maîtriser chaque mouvement avant de charger.");
  }
  sections.push({ key: "before", icon: Sun, title: "Avant la séance", tips: beforeTips, detailedTips: beforeDetailed });

  // ── After session ──
  const afterTips: string[] = [];
  const afterDetailed: string[] = [];
  afterTips.push("Retour au calme : 5 min de marche ou vélo léger.");
  if (isStrength) {
    afterTips.push("Étirements doux des groupes sollicités (30 s par muscle).");
    afterDetailed.push("Évite les étirements intenses statiques immédiatement après de la force lourde.");
  }
  if (hasLegs) {
    afterTips.push("Surélève les jambes 5 min si tu ressens des tensions.");
  }
  if (isLong) {
    afterTips.push("Réhydrate-toi rapidement : vise 500 ml dans les 30 min.");
    afterDetailed.push("Pour les efforts > 60 min, ajoute une pincée de sel ou une boisson isotonique.");
  } else {
    afterTips.push("Bois régulièrement dans l'heure qui suit.");
  }
  sections.push({ key: "after", icon: Moon, title: "Après la séance", tips: afterTips, detailedTips: afterDetailed });

  // ── Nutrition ──
  const nutriTips: string[] = [];
  const nutriDetailed: string[] = [];
  nutriTips.push("Repas ou collation riche en protéines dans les 2 h post-séance.");
  if (isStrength) {
    nutriTips.push("Vise 20–40 g de protéines (œufs, poulet, fromage blanc, légumineuses).");
    nutriDetailed.push("Associe des glucides complexes pour reconstituer le glycogène musculaire.");
  }
  if (isCardio) {
    nutriTips.push("Privilégie les glucides pour recharger : riz, pâtes, fruits.");
    nutriDetailed.push("Un ratio 3:1 glucides/protéines est optimal après un effort d'endurance.");
  }
  nutriTips.push("Préfère les aliments entiers aux suppléments quand c'est possible.");
  sections.push({ key: "nutrition", icon: Apple, title: "Nutrition", tips: nutriTips, detailedTips: nutriDetailed });

  // ── Recovery ──
  const recoveryTips: string[] = [];
  const recoveryDetailed: string[] = [];
  recoveryTips.push("Priorise le sommeil : 7–9 h par nuit, surtout les jours d'entraînement.");
  if (isStrength && hasLegs) {
    recoveryTips.push("Laisse 48–72 h avant de retravailler les mêmes groupes musculaires.");
  } else if (isStrength) {
    recoveryTips.push("48 h de repos minimum pour le même groupe musculaire.");
  }
  if (isLong) {
    recoveryTips.push("Envisage une journée de récupération active (marche, nage légère) le lendemain.");
  }
  recoveryDetailed.push("Évalue ta fatigue sur 10 avant chaque séance : au-dessus de 7/10, allège ou reporte.");
  recoveryDetailed.push("Le dimanche ou un jour fixe par semaine devrait être dédié au repos complet.");
  sections.push({ key: "recovery", icon: Heart, title: "Récupération", tips: recoveryTips, detailedTips: recoveryDetailed });

  // ── Injury / Precautions ──
  const injuryTips: string[] = [];
  const injuryDetailed: string[] = [];
  injuryTips.push("Règle d'or : si un mouvement provoque une douleur aiguë, arrête-le immédiatement.");
  if (hasLegs) {
    injuryTips.push("Attention aux ischio-jambiers : échauffe-les bien, surtout pour les squats et soulevés.");
  }
  if (hasUpperBody) {
    injuryTips.push("Protège tes épaules : contrôle la descente, évite les charges excessives au-dessus de la tête.");
  }
  if (isBeginner) {
    injuryTips.push("Maîtrise la technique avant d'augmenter la charge — la progression viendra naturellement.");
  }
  injuryDetailed.push("En cas de douleur persistante (> 48 h), consulte un professionnel de santé.");
  if (exerciseCount > 8) {
    injuryDetailed.push("Séance dense : sois vigilant sur la fatigue en fin de séance, c'est là que le risque augmente.");
  }
  sections.push({ key: "injury", icon: ShieldCheck, title: "Précautions & prévention", tips: injuryTips, detailedTips: injuryDetailed });

  // Filter out sections with zero tips (shouldn't happen but safety net)
  return sections.filter((s) => s.tips.length > 0);
}

export function SessionAdviceBlock({ session }: SessionAdviceBlockProps) {
  const { mode } = useInterfaceMode();
  const isSimple = mode === "simple";

  const sections = useMemo(() => deriveAdvice(session), [session]);

  if (sections.length === 0) return null;

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        Conseils pour ta séance
      </p>
      <Accordion type="multiple" className="space-y-1.5">
        {sections.map((section) => {
          const allTips = isSimple
            ? section.tips
            : [...section.tips, ...(section.detailedTips || [])];

          if (allTips.length === 0) return null;

          const Icon = section.icon;

          return (
            <AccordionItem
              key={section.key}
              value={section.key}
              className="rounded-lg border border-border bg-card overflow-hidden"
            >
              <AccordionTrigger className="px-4 py-2.5 text-sm hover:no-underline [&[data-state=open]>svg]:rotate-180">
                <span className="flex items-center gap-2 text-foreground font-medium text-xs">
                  <Icon className="h-4 w-4 text-primary shrink-0" />
                  {section.title}
                  <span className="text-[10px] text-muted-foreground font-normal">
                    {allTips.length} conseil{allTips.length > 1 ? "s" : ""}
                  </span>
                </span>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-3 pt-0">
                <ul className="space-y-1.5">
                  {allTips.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                      <span className="mt-1 h-1 w-1 rounded-full bg-primary/60 shrink-0" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}
